import api from './client';

// The documents controller wraps responses as `{ success, data, message }`.
// This helper transparently unwraps that envelope while still supporting
// endpoints (or future changes) that return the payload directly.
const unwrap = <T>(payload: any): T => {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    'data' in payload
  ) {
    return payload.data as T;
  }
  return payload as T;
};

// Enums
export enum DocumentType {
  HEALTH_CERTIFICATE = 'HEALTH_CERTIFICATE',
  VACCINATION_RECORD = 'VACCINATION_RECORD',
  DNA_SEXING_RESULT = 'DNA_SEXING_RESULT',
  BREEDING_RECORD = 'BREEDING_RECORD',
  SALE_RECEIPT = 'SALE_RECEIPT',
  PURCHASE_RECEIPT = 'PURCHASE_RECEIPT',
  TRANSFER_DOCUMENT = 'TRANSFER_DOCUMENT',
  CITES_PERMIT = 'CITES_PERMIT',
  OTHER = 'OTHER'
}

// Interfaces
export interface DocumentMetadata {
  issueDate?: string;
  expiryDate?: string;
  issuer?: string;
  reference?: string;
}

export interface Document {
  id: string;
  title: string;
  type: DocumentType;
  fileUrl: string;
  description?: string;
  tags?: string[];
  metadata?: DocumentMetadata;
  name?: string;
  path?: string;
  mimeType?: string;
  fileSize?: number;
  birdId?: string;
  healthTestId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDocumentDto {
  title: string;
  type: DocumentType;
  fileUrl: string;
  description?: string;
  tags?: string[];
  metadata?: DocumentMetadata;
  name?: string;
  path?: string;
  mimeType?: string;
  fileSize?: number;
  birdId?: string;
  healthTestId?: string;
}

export interface UpdateDocumentDto extends Partial<CreateDocumentDto> {}

export interface GetDocumentsDto {
  page?: number;
  limit?: number;
  search?: string;
  type?: DocumentType;
  birdId?: string;
  healthTestId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  tags?: string[];
  startDate?: string;
  endDate?: string;
}

export interface DocumentsResponse {
  success: boolean;
  data: Document[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface DocumentStats {
  total: number;
  byType: Record<string, number>;
  recentUploads: number;
  totalFileSize: number;
}

// API functions
export const documentsApi = {
  async getDocuments(filters: GetDocumentsDto = {}): Promise<Document[]> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          params.set(key, value.toString());
        }
      }
    });

    const response = await api.get<any>(`documents?${params.toString()}`);
    
    // Handle both array and paginated response formats
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && Array.isArray(response.data.documents)) {
      return response.data.documents;
    } else if (response.data && Array.isArray(response.data.data)) {
      return response.data.data;
    } else {
      return [];
    }
  },

  async getDocument(id: string): Promise<Document> {
    const response = await api.get<any>(`documents/${id}`);
    return unwrap<Document>(response.data);
  },

  async createDocument(data: CreateDocumentDto | FormData): Promise<Document> {
    const response = await api.post<{ success: boolean; data: Document } | Document>('documents', data);
    
    // Handle both response formats: { success: boolean, data: Document } or Document directly
    if ('success' in response.data && response.data.success && 'data' in response.data) {
      return response.data.data;
    }
    return response.data as Document;
  },

  async updateDocument(id: string, data: UpdateDocumentDto): Promise<Document> {
    const response = await api.patch<any>(`documents/${id}`, data);
    return unwrap<Document>(response.data);
  },

  async deleteDocument(id: string): Promise<void> {
    const response = await api.delete<void>(`documents/${id}`);
    return response.data;
  },

  async getStatistics(): Promise<DocumentStats> {
    const response = await api.get<any>('documents/statistics');
    return unwrap<DocumentStats>(response.data);
  },

  async getDocumentsByBird(birdId: string): Promise<Document[]> {
    const response = await api.get<any>(`documents/by-bird/${birdId}`);
    return unwrap<Document[]>(response.data) || [];
  },

  async getDocumentsByHealthTest(healthTestId: string): Promise<Document[]> {
    const response = await api.get<any>(`documents/by-health-test/${healthTestId}`);
    return unwrap<Document[]>(response.data) || [];
  },

  async getDocumentsByType(type: DocumentType): Promise<Document[]> {
    const response = await api.get<any>(`documents/by-type/${type}`);
    return unwrap<Document[]>(response.data) || [];
  }
};

// Helper functions
export const getDocumentTypeLabel = (type: DocumentType): string => {
  const labels: Record<DocumentType, string> = {
    [DocumentType.HEALTH_CERTIFICATE]: 'Health Certificate',
    [DocumentType.VACCINATION_RECORD]: 'Vaccination Record',
    [DocumentType.DNA_SEXING_RESULT]: 'DNA Sexing Result',
    [DocumentType.BREEDING_RECORD]: 'Breeding Record',
    [DocumentType.SALE_RECEIPT]: 'Sale Receipt',
    [DocumentType.PURCHASE_RECEIPT]: 'Purchase Receipt',
    [DocumentType.TRANSFER_DOCUMENT]: 'Transfer Document',
    [DocumentType.CITES_PERMIT]: 'CITES Permit',
    [DocumentType.OTHER]: 'Other'
  };
  return labels[type] || type;
};

export const getDocumentTypeColor = (type: DocumentType): string => {
  const colors: Record<DocumentType, string> = {
    [DocumentType.HEALTH_CERTIFICATE]: 'bg-green-100 text-green-800',
    [DocumentType.VACCINATION_RECORD]: 'bg-blue-100 text-blue-800',
    [DocumentType.DNA_SEXING_RESULT]: 'bg-purple-100 text-purple-800',
    [DocumentType.BREEDING_RECORD]: 'bg-pink-100 text-pink-800',
    [DocumentType.SALE_RECEIPT]: 'bg-yellow-100 text-yellow-800',
    [DocumentType.PURCHASE_RECEIPT]: 'bg-orange-100 text-orange-800',
    [DocumentType.TRANSFER_DOCUMENT]: 'bg-indigo-100 text-indigo-800',
    [DocumentType.CITES_PERMIT]: 'bg-red-100 text-red-800',
    [DocumentType.OTHER]: 'bg-gray-100 text-gray-800'
  };
  return colors[type] || 'bg-gray-100 text-gray-800';
};

export const formatFileSize = (bytes?: number): string => {
  if (!bytes) return 'Unknown size';
  
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
}; 